import { FC, useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';

import { BroadcastPushRequest } from '@joc/api-gateway';
import { ReactComponent as Radio } from 'assets/icons/checkbox/radio.svg';
import { ReactComponent as Unchecked } from 'assets/icons/checkbox/unchecked.svg';

import { API } from 'core/API';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputText from 'shared/components/FormInputs/InputText';
import InputErrorHint from 'shared/components/InputErrorHint';
import { SnackbarError, SnackbarSuccess } from 'shared/components/_Snackbars';

import OrganizationDropdown from './OrganizationDropdown';
import { CustomPushFormValues, PUSH_AUDIENCE } from './types';
import { MAX_PUSH_MESSAGE_LENGTH, MAX_PUSH_TITLE_LENGTH, customPushValidationSchema } from './validation';
import styles from './CustomPushNotifications.module.scss';

const INITIAL_VALUES: CustomPushFormValues = {
	title: '',
	body: '',
	audience: PUSH_AUDIENCE.ALL,
	organizationId: null,
};

const CustomPushNotifications: FC = () => {
	const [isSuccessOpen, setIsSuccessOpen] = useState(false);
	const [isErrorOpen, setIsErrorOpen] = useState(false);

	const handleSubmit = async (
		values: CustomPushFormValues,
		{ resetForm, validateForm }: FormikHelpers<CustomPushFormValues>
	) => {
		try {
			await API.broadcastPushNotifications(
				BroadcastPushRequest.fromJS({
					title: values.title.trim(),
					body: values.body.trim(),
					...(values.audience === PUSH_AUDIENCE.ORGANIZATION && values.organizationId
						? { organizationId: Number(values.organizationId) }
						: {}),
				})
			);
			const nextValues: CustomPushFormValues = {
				...values,
				title: '',
				body: '',
			};
			resetForm({ values: nextValues });
			validateForm(nextValues);
			setIsSuccessOpen(true);
		} catch {
			setIsErrorOpen(true);
		}
	};

	return (
		<>
			<Formik
				initialValues={INITIAL_VALUES}
				validationSchema={customPushValidationSchema}
				validateOnMount
				onSubmit={handleSubmit}
			>
				{({ values, isValid, isSubmitting, setFieldValue, setFieldTouched, setValues }) => (
					<Form className={styles.form}>
						<div className={styles.form__header}>
							<h2 className={styles.form__header_title}>Custom Push Notification</h2>
							<ButtonDefault
								submitType
								title="Send"
								classList={['primary', 'lg']}
								parentClassName={styles.form__header_button}
								disabled={!isValid || isSubmitting}
							/>
						</div>
						<div className={styles.form__item}>
							<span className={styles.form__item_label}>
								Audience<span className={styles.form__item_required}>*</span>
							</span>
							<div className={styles.form__audience}>
								<button
									type="button"
									className={styles.form__audienceOption}
									onClick={() => {
										setValues({
											...values,
											audience: PUSH_AUDIENCE.ALL,
											organizationId: null,
										});
									}}
								>
									{values.audience === PUSH_AUDIENCE.ALL ? <Radio /> : <Unchecked />}
									All Volunteers
								</button>
								<button
									type="button"
									className={styles.form__audienceOption}
									onClick={() => setFieldValue('audience', PUSH_AUDIENCE.ORGANIZATION)}
								>
									{values.audience === PUSH_AUDIENCE.ORGANIZATION ? <Radio /> : <Unchecked />}
									All Volunteers in an Organization
								</button>
							</div>
						</div>
						{values.audience === PUSH_AUDIENCE.ORGANIZATION && (
							<Field name="organizationId">
								{({ meta }: FieldProps<number | null, CustomPushFormValues>) => (
									<div className={styles.form__item}>
										<label className={styles.form__item_label}>
											Organization<span className={styles.form__item_required}>*</span>
										</label>
										<OrganizationDropdown
											value={values.organizationId}
											onChange={organizationId => {
												setFieldValue('organizationId', organizationId);
												setFieldTouched('organizationId', true, false);
											}}
											onBlur={() => setFieldTouched('organizationId', true)}
										/>
										{meta.error && meta.touched && (
											<InputErrorHint errorText={meta.error} disableMargin={true} />
										)}
									</div>
								)}
							</Field>
						)}
						<Field name="title">
							{({ field, meta, form }: FieldProps<string, CustomPushFormValues>) => (
								<div className={styles.form__item}>
									<label className={styles.form__item_label} htmlFor="custom-push-title">
										Title<span className={styles.form__item_required}>*</span>
									</label>
									<InputText
										id="custom-push-title"
										field={field}
										placeholder="Enter notification title"
										setFieldValue={form.setFieldValue}
										onBlur={() => setFieldTouched('title', true)}
									/>
									{!meta.error || !meta.touched ? (
										<span className={styles.form__counter}>
											Maximum characters: {field.value.trim().length}/{MAX_PUSH_TITLE_LENGTH}
										</span>
									) : null}
									{meta.error && meta.touched && (
										<InputErrorHint errorText={meta.error} disableMargin={true} />
									)}
								</div>
							)}
						</Field>

						<Field name="body">
							{({ field, meta }: FieldProps<string, CustomPushFormValues>) => (
								<div className={styles.form__item}>
									<label className={styles.form__item_label} htmlFor="custom-push-body">
										Message<span className={styles.form__item_required}>*</span>
									</label>
									<textarea
										{...field}
										id="custom-push-body"
										className={styles.form__message}
										placeholder="Enter notification message"
									/>
									{!meta.error || !meta.touched ? (
										<span className={styles.form__counter}>
											Maximum characters: {field.value.trim().length}/{MAX_PUSH_MESSAGE_LENGTH}
										</span>
									) : null}
									{meta.error && meta.touched && (
										<InputErrorHint errorText={meta.error} disableMargin={true} />
									)}
								</div>
							)}
						</Field>
					</Form>
				)}
			</Formik>
			<SnackbarSuccess
				open={isSuccessOpen}
				onClose={setIsSuccessOpen}
				message="Notification has been queued."
				width={320}
			/>
			<SnackbarError
				open={isErrorOpen}
				onClose={setIsErrorOpen}
				message="Something went wrong. Please try again."
				width={320}
			/>
		</>
	);
};

export default CustomPushNotifications;
